/**
 * UserDropdown Component
 * Dropdown menu for user actions with member and admin sections
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../hooks/useFeatures';
import Modal from './Modal/Modal';
import './UserDropdown.css';

interface UserDropdownProps {
  onLogout?: () => void;
}

/**
 * Check if user has admin privileges (moderator, admin, or super_admin)
 */
const hasAdminPrivileges = (user: { role: string } | null): boolean => {
  if (!user) return false;
  return ['moderator', 'admin', 'super_admin'].includes(user.role);
};

/**
 * Admin menu items configuration
 */
const getAdminMenuItems = (user: { role: string } | null) => {
  const items: Array<{ path: string; label: string; icon: string; divider?: boolean }> = [
    { path: '/admin', label: '管理後台', icon: '⚙️' },
    { path: '/admin/queue', label: '待審廣告', icon: '📋' },
    { path: '/admin/content', label: '內容管理', icon: '📝' },
    { path: '/admin/marquees', label: '跑馬燈管理', icon: '📢' },
    { path: '/admin/theme', label: '主題編輯器', icon: '🎨' },
  ];

  // E-commerce section (admin/super_admin only)
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    items.push({ path: '/admin/products', label: '商品管理', icon: '🛍️', divider: true });
    items.push({ path: '/admin/orders', label: '訂單管理', icon: '📦' });
    items.push({ path: '/admin/analytics', label: '統計分析', icon: '📊' });
  }

  // Member management (admin/super_admin only)
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    items.push({ path: '/admin/members', label: '會員管理', icon: '👥', divider: true });
  }

  // Bootstrap tools (super_admin only)
  if (user?.role === 'super_admin') {
    items.push({ path: '/admin/features', label: '功能開關管理', icon: '🔀', divider: true });
    items.push({ path: '/admin/system/bootstrap', label: 'Bootstrap 工具', icon: '🔧' });
  }

  return items;
};

/**
 * UserDropdown Component
 */
export const UserDropdown: React.FC<UserDropdownProps> = ({ onLogout }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Get feature flags for filtering admin menu items
  const features = useFeatures();

  // State for feature disabled modal
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [disabledFeatureName, setDisabledFeatureName] = useState('');

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
    if (event.key === 'Tab' && isOpen) {
      // Allow tab navigation but close dropdown on blur
      setTimeout(() => {
        if (document.activeElement?.closest('.user-dropdown') === null) {
          setIsOpen(false);
        }
      }, 0);
    }
  }, [isOpen]);

  /**
   * Toggle dropdown
   */
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    setIsOpen(false);
    onLogout?.();
  };

  /**
   * Handle menu item click
   */
  const handleMenuItemClick = (event: React.MouseEvent, path: string, label: string) => {
    // Check if feature is enabled
    if (!isFeatureEnabled(path)) {
      event.preventDefault();
      event.stopPropagation();
      // Show modal that feature is disabled
      setDisabledFeatureName(label);
      setFeatureModalOpen(true);
      return;
    }
    setIsOpen(false);
  };

  /**
   * Check if a feature is enabled for the given menu path
   * Based on feature-toggle-system-spec.md
   */
  const isFeatureEnabled = (path: string): boolean => {
    switch (path) {
      // Admin routes affected by feature toggles
      case '/admin/queue':
        // Requires ads.listing or wishes.listing to be enabled
        // If neither ads nor wishes are enabled, there's nothing to review
        return features.isAdsEnabled || features.isWishesEnabled;
      case '/admin/content':
        // Requires ads.listing or wishes.listing to be enabled
        // If neither ads nor wishes are enabled, there's no content to manage
        return features.isAdsEnabled || features.isWishesEnabled;
      case '/admin/products':
        return features.isProductsEnabled; // Requires products.listing
      case '/admin/orders':
        return features.isOrdersEnabled; // Requires shopping.orders
      case '/admin/analytics':
        // Analytics requires at least one e-commerce feature enabled
        return features.isProductsEnabled || features.isOrdersEnabled;
      // Admin routes not affected by feature toggles
      case '/admin':
      case '/admin/marquees':
      case '/admin/theme':
      case '/admin/members':
      case '/admin/features':
      case '/admin/system/bootstrap':
        return true;
      default:
        return true;
    }
  };

  /**
   * Get status badge for menu item
   */
  const getStatusBadge = (path: string): { text: string; className: string } | null => {
    if (isFeatureEnabled(path)) return null;
    return { text: '已停用', className: 'user-dropdown__status-badge--disabled' };
  };

  if (!user) {
    return null;
  }

  const adminMenuItems = getAdminMenuItems(user);
  const showAdminSection = hasAdminPrivileges(user);

  // Get role badge text
  const getRoleBadge = () => {
    switch (user?.role) {
      case 'super_admin':
        return { text: '超級管理員', className: 'user-dropdown__badge--super-admin' };
      case 'admin':
        return { text: '管理員', className: 'user-dropdown__badge--admin' };
      case 'moderator':
        return { text: '版主', className: 'user-dropdown__badge--moderator' };
      default:
        return null;
    }
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="user-dropdown" onKeyDown={handleKeyDown}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        className="user-dropdown__trigger"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="使用者選單"
      >
        <span className="user-dropdown__user-name">{user.name}</span>
        {roleBadge && (
          <span className={`user-dropdown__badge ${roleBadge.className}`}>
            {roleBadge.text}
          </span>
        )}
        <svg
          className={`user-dropdown__arrow ${isOpen ? 'user-dropdown__arrow--open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="user-dropdown__menu"
          ref={dropdownRef}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="user-dropdown-trigger"
        >
          {/* Member Section */}
          <div className="user-dropdown__section" role="group" aria-label="會員選項">
            <a
              href="/member"
              className="user-dropdown__item"
              role="menuitem"
              tabIndex={-1}
              onClick={() => setIsOpen(false)}
            >
              <span className="user-dropdown__item-icon">🏠</span>
              <span className="user-dropdown__item-label">會員中心</span>
            </a>
            <a
              href="/help"
              className="user-dropdown__item"
              role="menuitem"
              tabIndex={-1}
              onClick={() => setIsOpen(false)}
            >
              <span className="user-dropdown__item-icon">📖</span>
              <span className="user-dropdown__item-label">操作手冊</span>
            </a>
          </div>

          {/* Admin Section */}
          {showAdminSection && (
            <>
              <div className="user-dropdown__divider" role="separator" />
              <div className="user-dropdown__section" role="group" aria-label="管理選項">
                {adminMenuItems.map((item, index) => {
                  const featureEnabled = isFeatureEnabled(item.path);
                  const statusBadge = getStatusBadge(item.path);

                  return (
                    <React.Fragment key={item.path}>
                      {item.divider && index > 0 && (
                        <div className="user-dropdown__inner-divider" role="separator" />
                      )}
                      <a
                        href={item.path}
                        className={`user-dropdown__item ${!featureEnabled ? 'user-dropdown__item--disabled' : ''}`}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={(e) => handleMenuItemClick(e, item.path, item.label)}
                        aria-disabled={!featureEnabled}
                      >
                        <span className="user-dropdown__item-icon">{!featureEnabled ? '🔒' : item.icon}</span>
                        <span className="user-dropdown__item-label">{item.label}</span>
                        {statusBadge ? (
                          <span className={`user-dropdown__status-badge ${statusBadge.className}`}>
                            {statusBadge.text}
                          </span>
                        ) : null}
                      </a>
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}

          {/* Bottom Section */}
          <div className="user-dropdown__divider" role="separator" />
          <div className="user-dropdown__section" role="group" aria-label="其他選項">
            <button
              className="user-dropdown__item user-dropdown__item--logout"
              role="menuitem"
              tabIndex={-1}
              onClick={handleLogout}
            >
              <span className="user-dropdown__item-icon">🚪</span>
              <span className="user-dropdown__item-label">登出</span>
            </button>
          </div>
        </div>
      )}

      {/* Feature Disabled Modal */}
      <Modal
        isOpen={featureModalOpen}
        onClose={() => setFeatureModalOpen(false)}
        title="功能已停用"
        size="small"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</p>
          <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
            {disabledFeatureName}
          </p>
          <p style={{ color: 'var(--color-text-secondary, #6D5F52)', margin: '0' }}>
            該功能目前暫停服務
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--color-text-secondary, #6D5F52)' }}>
            請聯繫超級管理員開啟功能
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default UserDropdown;
